/**
 * WorkflowPicker — Shared workflow selection component.
 *
 * Used in 3 contexts:
 * 1. Workflow Viewer toolbar dropdown (filter='all', showBadges=true)
 * 2. Automation sidebar Process Tab (filter='executable', showBadges=false)
 * 3. Automation sidebar Workflows Tab (filter='all', showBadges=true, showFilterChips=true)
 *
 * Features:
 * - Search with "Create '{query}'" option
 * - Category-grouped expandable sections
 * - Optional readiness badges
 * - Optional filter chips (All/Ready)
 * - "+ New Workflow" creates a blank workflow directly — curated templates live in the marketplace
 */

import { notify } from '@affine/component';
import { useConnectivityStatus } from '@affine/core/components/hooks/use-connectivity-status';
import { useI18n } from '@affine/i18n';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { WorkflowInfo } from '../services/workflow';
import { usePersistedUiState } from '../utils/use-persisted-ui-state';
import * as styles from './workflow-picker.css';

// =============================================================================
// Types
// =============================================================================

export interface WorkflowPickerProps {
  /** All available workflows (unfiltered — component applies its own filters) */
  workflows: WorkflowInfo[];
  /** Currently selected workflow ID */
  selectedId?: string | null;
  /** Callback when a workflow is selected */
  onSelect: (workflow: WorkflowInfo) => void;
  /** Callback to create a new blank workflow. If undefined, create is disabled. */
  onCreate?: (name: string) => Promise<WorkflowInfo | null>;
  /** Keep create controls visible but block the action with offline feedback. */
  createUnavailable?: boolean;
  /** Callback to delete a workflow. If undefined, delete buttons are hidden. */
  onDelete?: (workflowId: string, workflowName: string) => void;

  /** Which workflows to show: 'all' or 'executable' (ready only) */
  filter?: 'all' | 'executable';
  /** Show readiness badges on each item */
  showBadges?: boolean;
  /** Show All/Ready filter chip bar */
  showFilterChips?: boolean;
  /** Whether the list is loading */
  isLoading?: boolean;
  /** Auto-focus the search input on mount */
  autoFocusSearch?: boolean;
  /** Footer info text */
  footerText?: string;
}

interface WorkflowPickerPersistedState {
  searchQuery: string;
  filterPublished: boolean;
  expandedCategories: string[];
}

const WORKFLOW_PICKER_STATE_KEY_PREFIX = 'streamlain.workflow.picker-state.v1';

const WORKFLOW_PICKER_STATE_FALLBACK: WorkflowPickerPersistedState = {
  searchQuery: '',
  filterPublished: false,
  expandedCategories: [],
};

function normalizeWorkflowPickerState(
  value: unknown
): WorkflowPickerPersistedState {
  const input =
    value && typeof value === 'object'
      ? (value as Partial<WorkflowPickerPersistedState>)
      : {};

  return {
    searchQuery: typeof input.searchQuery === 'string' ? input.searchQuery : '',
    filterPublished:
      typeof input.filterPublished === 'boolean'
        ? input.filterPublished
        : false,
    expandedCategories: Array.isArray(input.expandedCategories)
      ? input.expandedCategories.filter(
          (category): category is string => typeof category === 'string'
        )
      : [],
  };
}

// =============================================================================
// Component
// =============================================================================

export function WorkflowPicker({
  workflows,
  selectedId,
  onSelect,
  onCreate,
  createUnavailable = false,
  onDelete,
  filter = 'all',
  showBadges = false,
  showFilterChips = false,
  isLoading = false,
  autoFocusSearch = false,
  footerText,
}: WorkflowPickerProps) {
  const t = useI18n();
  const connectivityStatus = useConnectivityStatus();
  const searchRef = useRef<HTMLInputElement>(null);
  const [persistedState, setPersistedState] =
    usePersistedUiState<WorkflowPickerPersistedState>(
      `${WORKFLOW_PICKER_STATE_KEY_PREFIX}:${filter}:${showFilterChips ? 'panel' : 'dropdown'}`,
      WORKFLOW_PICKER_STATE_FALLBACK,
      normalizeWorkflowPickerState
    );
  const [searchQuery, setSearchQuery] = useState(persistedState.searchQuery);
  const [creating, setCreating] = useState(false);
  const isCreateUnavailable = connectivityStatus.offline || createUnavailable;
  const offlineCreateTitle =
    t['com.streamlain.streamlain.workflow.create.offline-title']();
  const offlineCreateMessage =
    t['com.streamlain.streamlain.workflow.create.offline-message']();
  const showOfflineCreateToast = useCallback(() => {
    notify.warning({
      title: offlineCreateTitle,
      message: offlineCreateMessage,
    });
  }, [offlineCreateMessage, offlineCreateTitle]);

  // Filter chips (only when showFilterChips=true)
  const [filterPublished, setFilterPublished] = useState(
    persistedState.filterPublished
  );

  // Category group collapse state — manual toggles tracked separately
  const [manualExpandedCategories, setManualExpandedCategories] = useState<
    Set<string>
  >(() => new Set(persistedState.expandedCategories));

  const updateSearchQuery = useCallback(
    (value: string) => {
      setSearchQuery(value);
      setPersistedState(prev => ({ ...prev, searchQuery: value }));
    },
    [setPersistedState]
  );

  const updateFilterPublished = useCallback(
    (value: boolean) => {
      setFilterPublished(value);
      setPersistedState(prev => ({ ...prev, filterPublished: value }));
    },
    [setPersistedState]
  );

  useEffect(() => {
    if (autoFocusSearch) {
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [autoFocusSearch]);

  // Step 1: Pre-filter by mode (executable = published only)
  const baseFiltered = useMemo(() => {
    if (filter === 'executable') {
      return workflows.filter(w => w.active);
    }
    return workflows;
  }, [workflows, filter]);

  // Step 2: Apply search + chip filters
  const filtered = useMemo(() => {
    let result = baseFiltered;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        w =>
          w.name.toLowerCase().includes(q) ||
          w.categories?.some(c => c.name.toLowerCase().includes(q))
      );
    }

    if (filterPublished) {
      result = result.filter(w => w.active);
    }

    return result;
  }, [baseFiltered, searchQuery, filterPublished]);

  // Group by categories
  const categoryGroups = useMemo(() => {
    const groups = new Map<string, WorkflowInfo[]>();

    for (const wf of filtered) {
      const catNames = wf.categories?.length
        ? wf.categories.map(c => c.name)
        : ['Uncategorized'];
      for (const catName of catNames) {
        const existing = groups.get(catName) ?? [];
        groups.set(catName, [...existing, wf]);
      }
    }

    return Array.from(groups.entries()).sort(([a], [b]) => {
      if (a === 'Uncategorized') return 1;
      if (b === 'Uncategorized') return -1;
      return a.localeCompare(b);
    });
  }, [filtered]);

  // Auto-expand groups with matches when search ≥ 3 chars
  const expandedCategories = useMemo(() => {
    if (searchQuery.trim().length >= 3) {
      return new Set(categoryGroups.map(([cat]) => cat));
    }
    return manualExpandedCategories;
  }, [searchQuery, categoryGroups, manualExpandedCategories]);

  // Categories start collapsed — user expands manually

  const toggleCategory = useCallback(
    (cat: string) => {
      setManualExpandedCategories(prev => {
        const next = new Set(prev);
        if (next.has(cat)) next.delete(cat);
        else next.add(cat);
        setPersistedState(state => ({
          ...state,
          expandedCategories: [...next],
        }));
        return next;
      });
    },
    [setPersistedState]
  );

  // Create a blank workflow directly — search text is used as the name if provided.
  const handleCreateClick = useCallback(
    async (name: string) => {
      if (!onCreate || creating) return;
      if (isCreateUnavailable) {
        showOfflineCreateToast();
        return;
      }
      setCreating(true);
      try {
        const workflow = await onCreate(name || 'New Workflow');
        if (workflow) {
          onSelect(workflow);
          updateSearchQuery('');
        }
      } catch (err) {
        console.error('[WorkflowPicker] Failed to create workflow:', err);
      } finally {
        setCreating(false);
      }
    },
    [
      onCreate,
      creating,
      isCreateUnavailable,
      showOfflineCreateToast,
      onSelect,
      updateSearchQuery,
    ]
  );

  const handleAllFilter = useCallback(() => {
    updateFilterPublished(false);
  }, [updateFilterPublished]);

  // Determine if "Create" row should show
  const showCreateRow = onCreate && searchQuery.trim().length > 0;

  return (
    <div
      className={clsx(
        styles.container,
        showFilterChips ? styles.containerPanel : styles.containerDropdown
      )}
    >
      {/* Search + Create Button */}
      <div className={styles.searchContainer}>
        <input
          ref={searchRef}
          type="text"
          className={styles.searchInput}
          placeholder="Search workflows..."
          value={searchQuery}
          onChange={e => updateSearchQuery(e.target.value)}
          onKeyDown={e => e.stopPropagation()}
        />
        {onCreate && !searchQuery.trim() && (
          <button
            className={styles.createButton}
            onClick={() => void handleCreateClick('')}
            disabled={creating}
            aria-disabled={isCreateUnavailable || creating}
            title={isCreateUnavailable ? offlineCreateMessage : undefined}
          >
            {creating ? 'Creating...' : '+ New Workflow'}
          </button>
        )}
      </div>

      {/* Filter Chips (only in panel mode / Workflows Tab) */}
      {showFilterChips && (
        <div className={styles.filterBar}>
          <button
            className={clsx(
              styles.filterChip,
              !filterPublished && styles.filterChipActive
            )}
            onClick={handleAllFilter}
          >
            All
          </button>
          <button
            className={clsx(
              styles.filterChip,
              filterPublished && styles.filterChipActive
            )}
            onClick={() => updateFilterPublished(!filterPublished)}
          >
            {t['com.streamlain.streamlain.workflow.page.active']()}
          </button>
        </div>
      )}

      {/* Current Workflow (pinned) */}
      {selectedId &&
        (() => {
          const current = workflows.find(w => w.id === selectedId);
          if (!current) return null;
          return (
            <div
              className={clsx(
                styles.workflowItem,
                styles.workflowItemSelected,
                styles.currentWorkflowRow
              )}
            >
              <div
                className={clsx(
                  styles.statusDot,
                  current.active
                    ? styles.statusDotActive
                    : styles.statusDotInactive
                )}
              />
              <div className={styles.workflowInfo}>
                <div className={styles.workflowName}>{current.name}</div>
                {showBadges && (
                  <div className={styles.workflowMeta}>
                    {current.active
                      ? t['com.streamlain.streamlain.workflow.page.active']()
                      : t['com.streamlain.streamlain.workflow.page.inactive']()}
                  </div>
                )}
              </div>
              {showBadges && (
                <div className={styles.badgeContainer}>
                  <span
                    className={
                      current.active
                        ? styles.badgePublished
                        : styles.badgeUnpublished
                    }
                  >
                    {current.active
                      ? t['com.streamlain.streamlain.workflow.page.active']()
                      : t['com.streamlain.streamlain.workflow.page.inactive']()}
                  </span>
                </div>
              )}
              {onDelete && (
                <span
                  className={styles.deleteBtn}
                  role="button"
                  tabIndex={0}
                  onClick={e => {
                    e.stopPropagation();
                    onDelete(current.id, current.name);
                  }}
                  title={`Delete ${current.name}`}
                >
                  ×
                </span>
              )}
            </div>
          );
        })()}

      {/* Divider after current */}
      {selectedId && <hr className={styles.currentDivider} />}

      {/* Workflow List */}
      <div className={styles.list}>
        {/* "Create '{query}'" row */}
        {showCreateRow && (
          <div
            className={styles.createFromSearchRow}
            onClick={() => void handleCreateClick(searchQuery.trim())}
            aria-disabled={isCreateUnavailable || creating}
            title={isCreateUnavailable ? offlineCreateMessage : undefined}
          >
            + Create &quot;{searchQuery.trim()}&quot;
          </div>
        )}

        {isLoading && baseFiltered.length === 0 ? (
          <>
            {[1, 2, 3].map(i => (
              <div key={i} className={styles.skeleton}>
                <div className={styles.skeletonDot} />
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                  }}
                >
                  <div
                    className={styles.skeletonLine}
                    style={{ width: '70%' }}
                  />
                  <div
                    className={styles.skeletonLine}
                    style={{ width: '40%', height: '8px' }}
                  />
                </div>
              </div>
            ))}
          </>
        ) : filtered.length === 0 && !showCreateRow ? (
          <div className={styles.emptyState}>
            <span>No workflows found</span>
            <span className={styles.emptySubtext}>
              {searchQuery
                ? 'Try a different search term'
                : filter === 'executable'
                  ? 'No ready workflows'
                  : 'Create workflows to see them here'}
            </span>
          </div>
        ) : (
          categoryGroups.map(([category, groupWorkflows]) => {
            const isExpanded = expandedCategories.has(category);
            return (
              <div key={category}>
                <div
                  className={styles.categoryGroupHeader}
                  onClick={() => toggleCategory(category)}
                >
                  <span
                    className={styles.categoryGroupChevron}
                    data-expanded={isExpanded}
                  >
                    ▶
                  </span>
                  {category}
                  <span className={styles.categoryGroupCount}>
                    ({groupWorkflows.length})
                  </span>
                </div>
                {isExpanded &&
                  groupWorkflows.map(workflow => (
                    <div
                      key={workflow.id}
                      className={clsx(
                        styles.workflowItem,
                        workflow.id === selectedId &&
                          styles.workflowItemSelected
                      )}
                      onClick={() => onSelect(workflow)}
                    >
                      <div
                        className={clsx(
                          styles.statusDot,
                          workflow.active
                            ? styles.statusDotActive
                            : styles.statusDotInactive
                        )}
                      />
                      <div className={styles.workflowInfo}>
                        <div className={styles.workflowName}>
                          {workflow.name}
                        </div>
                        {showBadges && (
                          <div className={styles.workflowMeta}>
                            {workflow.active
                              ? t[
                                  'com.streamlain.streamlain.workflow.page.active'
                                ]()
                              : t[
                                  'com.streamlain.streamlain.workflow.page.inactive'
                                ]()}
                          </div>
                        )}
                      </div>
                      {showBadges && (
                        <div className={styles.badgeContainer}>
                          <span
                            className={
                              workflow.active
                                ? styles.badgePublished
                                : styles.badgeUnpublished
                            }
                          >
                            {workflow.active
                              ? t[
                                  'com.streamlain.streamlain.workflow.page.active'
                                ]()
                              : t[
                                  'com.streamlain.streamlain.workflow.page.inactive'
                                ]()}
                          </span>
                        </div>
                      )}
                      {onDelete && (
                        <span
                          className={styles.deleteBtn}
                          role="button"
                          tabIndex={0}
                          onClick={e => {
                            e.stopPropagation();
                            onDelete(workflow.id, workflow.name);
                          }}
                          title={`Delete ${workflow.name}`}
                        >
                          ×
                        </span>
                      )}
                    </div>
                  ))}
              </div>
            );
          })
        )}
      </div>

      {/* Footer info */}
      {footerText && <div className={styles.footerInfo}>{footerText}</div>}
    </div>
  );
}
